import type { RefObject } from 'react';
import { type Order, type OrderItem, getOrderById } from '../../api/order';
import {
  type ProductionItem,
  getProductionItems,
} from '../../api/productionItems';

export type JobOrder = {
  order: Order;
  item: OrderItem;
};

export interface JobOrderPrintHandle {
  whenReady: () => Promise<void>;
}

const AFTER_PRINT_TIMEOUT_MS = 60_000;

const waitForNextPaint = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });

/** Resolves once every image in `container` has loaded or failed. */
export const waitForImages = async (container: HTMLElement) => {
  const images = [...container.querySelectorAll('img')];
  await Promise.all(
    images.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete) {
            resolve();
            return;
          }
          const done = () => resolve();
          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true });
        })
    )
  );
};

const waitForAfterPrint = () =>
  new Promise<void>((resolve) => {
    const done = () => {
      window.removeEventListener('afterprint', done);
      clearTimeout(fallback);
      resolve();
    };
    window.addEventListener('afterprint', done);
    const fallback = setTimeout(done, AFTER_PRINT_TIMEOUT_MS);
  });

export const runBrowserPrint = async (
  ref: RefObject<JobOrderPrintHandle | null>
) => {
  await waitForNextPaint();
  for (let attempt = 0; attempt < 20; attempt += 1) {
    if (ref.current) {
      await ref.current.whenReady();
      break;
    }
    await waitForNextPaint();
  }
  const afterPrint = waitForAfterPrint();
  window.print();
  await afterPrint;
};

const buildJobOrders = async (
  productionItems: ProductionItem[]
): Promise<JobOrder[]> => {
  const ordersById = new Map(
    (
      await Promise.all(
        [...new Set(productionItems.map((item) => item.order.id))].map(
          getOrderById
        )
      )
    ).map((order) => [order.id, order])
  );

  const jobOrders = productionItems.flatMap((productionItem) => {
    const order = ordersById.get(productionItem.order.id);
    const item = order?.items.find((i) => i.id === productionItem.id);
    return order && item ? [{ order, item }] : [];
  });

  if (jobOrders.length === 0) {
    throw new Error('Failed to load job orders for printing');
  }

  return jobOrders;
};

export const loadQueuedJobOrders = async (): Promise<{
  jobOrders: JobOrder[];
  itemIds: number[];
} | null> => {
  const queued = (await getProductionItems()).filter(
    (item) => item.printQueuedAt
  );
  if (queued.length === 0) {
    return null;
  }

  return {
    jobOrders: await buildJobOrders(queued),
    itemIds: queued.map((item) => item.id),
  };
};

/** Items whose order has left production (e.g. released) are skipped. */
export const loadJobOrdersByItemIds = async (
  itemIds: number[]
): Promise<{
  jobOrders: JobOrder[];
  itemIds: number[];
} | null> => {
  const idSet = new Set(itemIds);
  const productionItems = (await getProductionItems()).filter((item) =>
    idSet.has(item.id)
  );
  if (productionItems.length === 0) {
    return null;
  }

  return {
    jobOrders: await buildJobOrders(productionItems),
    itemIds: productionItems.map((item) => item.id),
  };
};

export const formatItemIdList = (itemIds: number[]): string => {
  if (itemIds.length === 0) return '';
  if (itemIds.length === 1) return `#${itemIds[0]}`;
  const formatted = itemIds.map((id) => `#${id}`);
  return formatted.length === 2
    ? `${formatted[0]} and ${formatted[1]}`
    : `${formatted.slice(0, -1).join(', ')} and ${formatted[formatted.length - 1]}`;
};
